import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as svc from './excepciones-service';
import { excepcionesKeys } from './keys';
import type {
  CreateExcepcionDto,
  UpdateExcepcionDto,
  QueryExcepcionesDto,
} from './excepciones-service';

export function useExcepcionesList(params: QueryExcepcionesDto) {
  return useQuery({
    queryKey: excepcionesKeys.list(params as unknown as Record<string, unknown>),
    queryFn: () => svc.getExcepciones(params),
    staleTime: 30_000,
  });
}

export function useExcepcionesStats(params: Omit<QueryExcepcionesDto, 'offset' | 'limit'>) {
  return useQuery({
    queryKey: excepcionesKeys.stats(params as unknown as Record<string, unknown>),
    queryFn: () => svc.getExcepcionesStats(params),
    staleTime: 30_000,
  });
}

export function useExcepcion(id: number) {
  return useQuery({
    queryKey: excepcionesKeys.detail(id),
    queryFn: () => svc.getExcepcion(id),
    enabled: id > 0,
    staleTime: 30_000,
  });
}

export function useCreateExcepcion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateExcepcionDto) => svc.createExcepcion(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: excepcionesKeys.lists() });
    },
  });
}

export function useUpdateExcepcion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateExcepcionDto }) =>
      svc.updateExcepcion(id, dto),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: excepcionesKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: excepcionesKeys.lists() });
    },
  });
}

export function useDeleteExcepcion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => svc.deleteExcepcion(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: excepcionesKeys.lists() });
    },
  });
}
